import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import Select2Dropdown from '../common/Select2Dropdown';
import useSortableTable from '../common/useSortableTable';
import { usePermissions } from '../../services/PermissionsContext';
import { APP_MODULES_HIERARCHICAL } from '../../appConfig';
import { CLIENT_MENU_MODULES } from '../../clientMenuConfig';
import { TRANSPORTER_MENU_MODULES } from '../../transporterMenuConfig';

const API_URL = '/api/admin/';
const MASTERS_API_URL = '/api/masters/';

const formatStaffName = (staff) => {
    if (!staff) return '';
    const middle = staff.middle_name ? `${staff.middle_name.charAt(0)}.` : '';
    return `${staff.first_name} ${middle} ${staff.last_name}`.replace(/\s+/g, ' ').trim();
};

const UserManagement = ({ initialTab = 'STAFF', hideTabs = false, pageTitle = 'User Management', hideSecurity = false }) => {
    const { hasPermission, permissions } = usePermissions();
    const isSuperuser = permissions?.is_superuser;

    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [permissionsUser, setPermissionsUser] = useState(null);
    
    // Tabs state
    const [activeTab, setActiveTab] = useState(initialTab);

    const canViewPage = useMemo(() => {
        if (hasPermission('USER MANAGEMENT', 'can_view')) return true;
        if (activeTab === 'CUSTOMER' && hasPermission('CUSTOMER PORTAL', 'can_view')) return true;
        if (activeTab === 'TRANSPORTER' && hasPermission('TRANSPORTER USER MANAGEMENT', 'can_view')) return true;
        return false;
    }, [hasPermission, activeTab]);

    // Filters (removed user_type as it is now handled by tabs)
    const [filters, setFilters] = useState({ username: '', is_active: '' });

    // Data for dropdowns
    const [staffList, setStaffList] = useState([]);
    const [customerList, setCustomerList] = useState([]);
    const [transporterList, setTransporterList] = useState([]);

    // Security settings modal
    const [isSecurityModalOpen, setIsSecurityModalOpen] = useState(false);
    const [securitySettings, setSecuritySettings] = useState({ company_ip_address: '' });
    const [securityUsers, setSecurityUsers] = useState({ left: [], right: [] });
    const [selectedLeftUsers, setSelectedLeftUsers] = useState([]);
    const [selectedRightUsers, setSelectedRightUsers] = useState([]);
    const [savingSecurity, setSavingSecurity] = useState(false);

    const fetchUsers = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}users/`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to fetch users');
            const data = await res.json();
            setUsers(data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchDropdownData = useCallback(async (type) => {
        try {
            let url, setter;
            if (type === 'STAFF') { url = `${API_URL}staff/`; setter = setStaffList; } 
            else if (type === 'CUSTOMER') { url = `${MASTERS_API_URL}customers/`; setter = setCustomerList; } 
            else if (type === 'TRANSPORTER') { url = `${MASTERS_API_URL}transporters/`; setter = setTransporterList; }
            
            if (url) {
                const res = await fetch(url, { headers: getAuthHeaders() });
                const data = await res.json();
                setter(data);
            }
        } catch (error) {
            toast.error(`Failed to load ${type.toLowerCase()} list.`);
        }
    }, []);

    useEffect(() => {
        if (canViewPage) {
            fetchUsers();
            fetchDropdownData('STAFF');
            fetchDropdownData('CUSTOMER');
            fetchDropdownData('TRANSPORTER');
        }
    }, [fetchUsers, fetchDropdownData, canViewPage]);

    const getLinkedName = (user) => {
        if (!user.profile) return 'N/A';
        
        // Use activeTab mapping in case user_type is not strictly set via admin panel
        let typeToUse = activeTab;
        if (!typeToUse || typeToUse === 'All') {
            typeToUse = user.profile?.user_type;
        }

        const linked_staff = user.profile?.linked_staff || user.staff_id;
        const linked_customer = user.profile?.linked_customer;
        const linked_transporter = user.profile?.linked_transporter;
        let list, id;

        switch (typeToUse) {
            case 'STAFF': list = staffList; id = linked_staff; break;
            case 'CUSTOMER': list = customerList; id = linked_customer; break;
            case 'TRANSPORTER': list = transporterList; id = linked_transporter; break;
            default: return 'N/A';
        }
        
        if (!id || list.length === 0) return '...';
        const linkedEntity = list.find(item => item.id === id);
        if (!linkedEntity) return `ID: ${id} (Not found)`;
        return typeToUse === 'STAFF' ? formatStaffName(linkedEntity) : linkedEntity.name;
    };

    const getDepartment = (user) => {
        if (activeTab !== 'STAFF') return '-';
        const linkedStaffId = user.profile?.linked_staff || user.staff_id;
        if (!linkedStaffId) return '-';
        
        const staff = staffList.find(s => s.id === linkedStaffId);
        return staff ? staff.department_name : '-';
    };

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            const { username, is_active } = filters;
            const term = username.toLowerCase();
            const searchMatch = username === '' ||
                user.username.toLowerCase().includes(term) ||
                String(user.id).includes(term) ||
                getLinkedName(user).toLowerCase().includes(term);
            
            // Filter by Tab (User Type)
            let typeMatch = false;
            
            if (activeTab === 'STAFF') {
                // If created in Django Admin without setting user_type, they might just have a linked_staff or a direct staff_id mapped from the Staff record
                typeMatch = user.profile?.linked_staff != null || user.staff_id != null;
            } else {
                typeMatch = user.profile?.user_type === activeTab;
            }

            return (
                typeMatch &&
                searchMatch &&
                (is_active === '' || String(user.is_active) === is_active)
            );
        });
    }, [users, filters, activeTab, staffList, customerList, transporterList]);

    const handleOpenModal = (user = null) => {
        const customerModule = activeTab === 'CUSTOMER';
        const transporterModule = activeTab === 'TRANSPORTER';
        const canOpen = user
            ? (customerModule
                ? (hasPermission('CUSTOMER PORTAL', 'can_edit') || hasPermission('USER MANAGEMENT', 'can_edit'))
                : transporterModule
                    ? (hasPermission('TRANSPORTER USER MANAGEMENT', 'can_edit') || hasPermission('USER MANAGEMENT', 'can_edit'))
                    : hasPermission('USER MANAGEMENT', 'can_edit'))
            : (customerModule
                ? (hasPermission('CUSTOMER PORTAL', 'can_create') || hasPermission('USER MANAGEMENT', 'can_create'))
                : transporterModule
                    ? (hasPermission('TRANSPORTER USER MANAGEMENT', 'can_create') || hasPermission('USER MANAGEMENT', 'can_create'))
                    : hasPermission('USER MANAGEMENT', 'can_create'));
        if (!canOpen) {
            toast.error("You do not have permission to perform this action.");
            return;
        }
        setEditingUser(user);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingUser(null);
    };

    const handleOpenPermissionsModal = (user) => {
        const canOpenPermissions = activeTab === 'CUSTOMER'
            ? (hasPermission('CUSTOMER PORTAL', 'can_edit') || hasPermission('USER MANAGEMENT', 'can_edit'))
            : activeTab === 'TRANSPORTER'
                ? (hasPermission('TRANSPORTER USER MANAGEMENT', 'can_view') || hasPermission('TRANSPORTER USER MANAGEMENT', 'can_edit') || hasPermission('USER MANAGEMENT', 'can_edit'))
                : hasPermission('USER MANAGEMENT', 'can_edit');
        if (!canOpenPermissions) {
            toast.error("You do not have permission to view user permissions.");
            return;
        }
        setPermissionsUser(user);
        setIsPermissionsModalOpen(true);
    };

    const handleClosePermissionsModal = () => {
        setIsPermissionsModalOpen(false);
        setPermissionsUser(null);
    };

    const handleLoginAsUser = async (user) => {
        if (!isSuperuser) {
            toast.error("Only SuperAdmin can login as another user.");
            return;
        }
        try {
            const res = await fetch(`${API_URL}users/${user.id}/impersonate/`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.detail || 'Failed to login as user.');
            }
            const params = new URLSearchParams({
                impersonate: '1',
                access: data.access,
                refresh: data.refresh || '',
            });
            const popout = window.open(
                `${window.location.origin}/?${params.toString()}`,
                `impersonate_${user.id}`,
                'width=1400,height=900'
            );
            if (!popout) {
                toast.error('Popup blocked. Please allow popups for this site to use "Login as User".');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to login as user.');
        }
    };

    const handleSave = (newUser, tempPassword) => {
        fetchUsers();
        handleCloseModal();
        if (tempPassword) {
            toast.info(`Temporary Password: ${tempPassword}`, {
                autoClose: 60000,
                closeOnClick: false,
                draggable: false,
                position: "top-center",
                icon: "🔑",
                render: `User Created! Temp Password: ${tempPassword}. Please copy it securely.`
            });
        }
    };

    const handleResetPassword = async (userId, username) => {
        const canReset = activeTab === 'CUSTOMER'
            ? (hasPermission('CUSTOMER PORTAL', 'can_delete') || hasPermission('USER MANAGEMENT', 'can_delete'))
            : hasPermission('USER MANAGEMENT', 'can_delete');
        if (!canReset) {
            toast.error("You do not have permission to reset passwords.");
            return;
        }
        if (!window.confirm(`Are you sure you want to reset the password for user '${username}'? A new temporary password will be generated and emailed.`)) {
            return;
        }
        try {
            const res = await fetch(`${API_URL}users/${userId}/reset-password/`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error('Failed to reset password');
            const data = await res.json();
            toast.success(`Password for '${username}' has been reset.`);
            toast.info(`New Temporary Password: ${data.temp_password}`, {
                autoClose: 60000,
                closeOnClick: false,
                draggable: false,
                position: "top-center",
                icon: "🔑",
                render: `Password Reset! New Temp Password: ${data.temp_password}. Please copy it securely.`
            });
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleDelete = async (userId, username) => {
        const canDelete = activeTab === 'CUSTOMER'
            ? (hasPermission('CUSTOMER PORTAL', 'can_delete') || hasPermission('USER MANAGEMENT', 'can_delete'))
            : hasPermission('USER MANAGEMENT', 'can_delete');
        if (!canDelete) {
            toast.error("You do not have permission to delete this user.");
            return;
        }
        if (!window.confirm(`Are you sure you want to delete user '${username}'? This action cannot be undone.`)) {
            return;
        }
        try {
            const res = await fetch(`${API_URL}users/${userId}/`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error('Failed to delete user');
            toast.success(`User '${username}' deleted successfully.`);
            fetchUsers();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const sortValueResolver = useCallback((user, key) => {
        if (key === 'linked_name') return getLinkedName(user);
        if (key === 'department_name') return getDepartment(user);
        if (key === 'full_name') return user.first_name || '';
        return user[key];
    }, [activeTab, staffList, customerList, transporterList]);

    const { items: sortedFilteredUsers, requestSort, getSortIndicator } = useSortableTable(
        filteredUsers,
        { key: 'linked_name', direction: 'ascending' },
        sortValueResolver
    );

    const statusOptions = [{ id: '', text: 'All' }, { id: 'true', text: 'Active' }, { id: 'false', text: 'Inactive' }];

    const openSecurityModal = async () => {
        setIsSecurityModalOpen(true);
        try {
            // Fetch security settings
            const settingsRes = await fetch(`${API_URL}security-settings/`, { headers: getAuthHeaders() });
            if (settingsRes.ok) {
                const settingsData = await settingsRes.json();
                setSecuritySettings(settingsData);
            }

            // Fetch all users with their allow_outside_access status
            const usersRes = await fetch(`${API_URL}users/`, { headers: getAuthHeaders() });
            if (usersRes.ok) {
                const usersData = await usersRes.json();
                // Filter out superadmin users and only show active users
                const filteredUsers = usersData.filter(u => !u.is_superuser && u.is_active);
                const leftUsers = filteredUsers.filter(u => !u.allow_outside_access).map(u => ({ id: u.id, username: u.username, name: u.first_name }));
                const rightUsers = filteredUsers.filter(u => u.allow_outside_access).map(u => ({ id: u.id, username: u.username, name: u.first_name }));
                setSecurityUsers({ left: leftUsers, right: rightUsers });
            }
        } catch (error) {
            toast.error('Failed to load security settings');
        }
    };

    const closeSecurityModal = () => {
        setIsSecurityModalOpen(false);
        setSecuritySettings({ company_ip_address: '' });
        setSecurityUsers({ left: [], right: [] });
        setSelectedLeftUsers([]);
        setSelectedRightUsers([]);
    };

    const handleIPChange = (e) => {
        let value = e.target.value.replace(/[^0-9.]/g, '');

        // Split by dots and validate each octet
        const octets = value.split('.').filter(octet => octet !== '');
        const validOctets = octets.map(octet => {
            // Limit each octet to 3 digits and max value of 255
            let num = parseInt(octet.slice(0, 3), 10);
            if (isNaN(num)) num = 0;
            if (num > 255) num = 255;
            return num.toString();
        });

        // Join with dots, max 4 octets
        let formatted = validOctets.slice(0, 4).join('.');

        setSecuritySettings({ ...securitySettings, company_ip_address: formatted });
    };

    const validateIP = (ip) => {
        // IPv4 validation regex
        const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/;
        if (!ipv4Pattern.test(ip)) return false;

        const octets = ip.split('.');
        for (const octet of octets) {
            const num = parseInt(octet, 10);
            if (num < 0 || num > 255) return false;
        }
        return true;
    };

    const moveUsersRight = () => {
        const usersToMove = securityUsers.left.filter(u => selectedLeftUsers.includes(u.id));
        setSecurityUsers({
            left: securityUsers.left.filter(u => !selectedLeftUsers.includes(u.id)),
            right: [...securityUsers.right, ...usersToMove]
        });
        setSelectedLeftUsers([]);
    };

    const moveUsersLeft = () => {
        const usersToMove = securityUsers.right.filter(u => selectedRightUsers.includes(u.id));
        setSecurityUsers({
            left: [...securityUsers.left, ...usersToMove],
            right: securityUsers.right.filter(u => !selectedRightUsers.includes(u.id))
        });
        setSelectedRightUsers([]);
    };

    const saveSecuritySettings = async () => {
        // Validate IP address
        if (!securitySettings.company_ip_address) {
            toast.error('Office IP Address is required');
            return;
        }

        if (!validateIP(securitySettings.company_ip_address)) {
            toast.error('Invalid IPv4 address format. Please enter a valid IP address (e.g., 192.168.1.1)');
            return;
        }

        setSavingSecurity(true);
        try {
            // Save security settings
            const settingsUrl = securitySettings.id ? `${API_URL}security-settings/${securitySettings.id}/` : `${API_URL}security-settings/`;
            const settingsRes = await fetch(settingsUrl, {
                method: securitySettings.id ? 'PUT' : 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(securitySettings)
            });
            if (!settingsRes.ok) throw new Error('Failed to save security settings');

            // Update user allow_outside_access using bulk update endpoint
            const rightUserIds = securityUsers.right.map(u => u.id);
            const leftUserIds = securityUsers.left.map(u => u.id);

            const updatePromises = [];

            if (rightUserIds.length > 0) {
                updatePromises.push(
                    fetch(`${API_URL}security-settings/bulk-update-outside-access/`, {
                        method: 'POST',
                        headers: getAuthHeaders(),
                        body: JSON.stringify({ user_ids: rightUserIds, allow_outside_access: true })
                    })
                );
            }

            if (leftUserIds.length > 0) {
                updatePromises.push(
                    fetch(`${API_URL}security-settings/bulk-update-outside-access/`, {
                        method: 'POST',
                        headers: getAuthHeaders(),
                        body: JSON.stringify({ user_ids: leftUserIds, allow_outside_access: false })
                    })
                );
            }

            await Promise.all(updatePromises);

            toast.success('Security settings saved successfully');
            closeSecurityModal();
            fetchUsers();
        } catch (error) {
            toast.error('Failed to save security settings');
        } finally {
            setSavingSecurity(false);
        }
    };

    // Tab definitions
    const tabs = [
        { id: 'STAFF', label: 'Staff Users' },
        { id: 'CUSTOMER', label: 'Customer Users' },
        { id: 'TRANSPORTER', label: 'Transporter Users' },
        { id: 'INDEPENDENT', label: 'Independent Users' }
    ];

    if (!canViewPage) {
        return (
            <div className="page-container">
                <div className="admin-header">
                    <h2>Permission Denied</h2>
                    <p>You do not have permission to view this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="admin-header">
                <h2>{pageTitle}</h2>
            </div>

            {/* Tabs */}
            <div className="custom-tabs-container" style={{ marginTop: 16, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {!hideTabs && (
                    <div style={{ display: 'flex' }}>
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`custom-tab ${activeTab === tab.id ? 'active' : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}
                {!hideSecurity && (
                    <button
                        className="icon-btn"
                        onClick={() => openSecurityModal()}
                        title="Security Settings"
                        style={{ fontSize: '18px' }}
                    >
                        <i className="fa fa-cog" />
                    </button>
                )}
            </div>

            <div className="filters-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 16 }}>
                    <div className="filter-item">
                        <input type="text" value={filters.username} onChange={e => handleFilterChange('username', e.target.value)} placeholder="Filter by username or Linked to..." className="search-input" />
                    </div>
                    <div className="filter-item" style={{ width: 150 }}>
                        <Select2Dropdown 
                            options={statusOptions} 
                            value={filters.is_active} 
                            onChange={val => handleFilterChange('is_active', val)} 
                            placeholder="Status" 
                            valueKey="id" 
                            displayKey="text" 
                        />
                    </div>
                </div>
                
                {(hasPermission('USER MANAGEMENT', 'can_create') || (activeTab === 'TRANSPORTER' && hasPermission('TRANSPORTER USER MANAGEMENT', 'can_create'))) && (
                    <button className="add-btn" onClick={() => handleOpenModal()}>
                        <i className="fa fa-plus" style={{ marginRight: 8 }} /> 
                        Add New {tabs.find(t => t.id === activeTab)?.label.replace('Users', '')} User
                    </button>
                )}
            </div>

            {isLoading ? <p>Loading...</p> : (
                <div className="table-responsive">
                    <style>{`
                        .admin-table tbody tr.hover-highlight:hover {
                            background-color: #f0f7ff !important;
                        }
                    `}</style>
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th onClick={() => requestSort('username')} style={{ cursor: 'pointer' }}>Username {getSortIndicator('username')}</th>
                                {activeTab !== 'INDEPENDENT' && <th onClick={() => requestSort('linked_name')} style={{ cursor: 'pointer' }}>Linked To {getSortIndicator('linked_name')}</th>}
                                {activeTab === 'STAFF' && <th onClick={() => requestSort('department_name')} style={{ cursor: 'pointer' }}>Department {getSortIndicator('department_name')}</th>}
                                {activeTab === 'INDEPENDENT' && <th onClick={() => requestSort('full_name')} style={{ cursor: 'pointer' }}>Full Name {getSortIndicator('full_name')}</th>}
                                {activeTab === 'INDEPENDENT' && <th onClick={() => requestSort('email')} style={{ cursor: 'pointer' }}>Email {getSortIndicator('email')}</th>}
                                <th onClick={() => requestSort('is_active')} style={{ cursor: 'pointer' }}>Status {getSortIndicator('is_active')}</th>
                                {(hasPermission('USER MANAGEMENT', 'can_edit') || hasPermission('USER MANAGEMENT', 'can_delete') || isSuperuser || (activeTab === 'CUSTOMER' && (hasPermission('CUSTOMER PORTAL', 'can_edit') || hasPermission('CUSTOMER PORTAL', 'can_delete'))) || (activeTab === 'TRANSPORTER' && (hasPermission('TRANSPORTER USER MANAGEMENT', 'can_view') || hasPermission('TRANSPORTER USER MANAGEMENT', 'can_edit')))) && (
                                    <th style={{ width: '140px', textAlign: 'center' }}>Actions</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.length === 0 && <tr><td colSpan={6} className="no-data">No users found in this category.</td></tr>}
                            {sortedFilteredUsers.map(user => (
                                <tr key={user.id} className="hover-highlight">
                                    <td>{user.username}</td>
                                    {activeTab !== 'INDEPENDENT' && <td>{getLinkedName(user)}</td>}
                                    {activeTab === 'STAFF' && <td>{getDepartment(user)}</td>}
                                    {activeTab === 'INDEPENDENT' && <td>{user.first_name}</td>}
                                    {activeTab === 'INDEPENDENT' && <td>{user.email}</td>}
                                    <td><span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>{user.is_active ? 'Active' : 'Inactive'}</span></td>
                                    {(hasPermission('USER MANAGEMENT', 'can_edit') || hasPermission('USER MANAGEMENT', 'can_delete') || isSuperuser || (activeTab === 'CUSTOMER' && (hasPermission('CUSTOMER PORTAL', 'can_edit') || hasPermission('CUSTOMER PORTAL', 'can_delete'))) || (activeTab === 'TRANSPORTER' && (hasPermission('TRANSPORTER USER MANAGEMENT', 'can_view') || hasPermission('TRANSPORTER USER MANAGEMENT', 'can_edit')))) && (
                                        <td className="actions" style={{ textAlign: 'center' }}>
                                            {(hasPermission('USER MANAGEMENT', 'can_edit') || (activeTab === 'TRANSPORTER' && hasPermission('TRANSPORTER USER MANAGEMENT', 'can_edit'))) && (
                                                <button className="icon-btn" title="Edit User" onClick={() => handleOpenModal(user)}>
                                                    <i className="fa fa-pen" />
                                                </button>
                                            )}
                                            {hasPermission('USER MANAGEMENT', 'can_delete') && (
                                                <button className="icon-btn" title="Reset Password" onClick={() => handleResetPassword(user.id, user.username)}>
                                                    <i className="fa fa-key" />
                                                </button>
                                            )}
                                            {(hasPermission('USER MANAGEMENT', 'can_edit') || (activeTab === 'TRANSPORTER' && (hasPermission('TRANSPORTER USER MANAGEMENT', 'can_view') || hasPermission('TRANSPORTER USER MANAGEMENT', 'can_edit')))) && (
                                                <button className="icon-btn" title={user.has_custom_permissions ? 'User Permissions (Custom)' : 'User Permissions'} onClick={() => handleOpenPermissionsModal(user)}>
                                                    <i className="fa fa-shield-alt" style={user.has_custom_permissions ? { color: '#dc3545' } : undefined} />
                                                </button>
                                            )}
                                            {isSuperuser && (
                                                <button className="icon-btn" title="Login as User" onClick={() => handleLoginAsUser(user)}>
                                                    <i className="fa fa-sign-in-alt" />
                                                </button>
                                            )}
                                            {activeTab !== 'STAFF' && (activeTab === 'CUSTOMER' ? (hasPermission('CUSTOMER PORTAL', 'can_delete') || hasPermission('USER MANAGEMENT', 'can_delete')) : hasPermission('USER MANAGEMENT', 'can_delete')) && (
                                                <button className="icon-btn" title="Delete User" onClick={() => handleDelete(user.id, user.username)} style={{ color: '#dc3545' }}>
                                                    <i className="fa fa-trash" />
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {isModalOpen && (
                <UserModal
                    user={editingUser}
                    activeTab={activeTab}
                    onClose={handleCloseModal}
                    onSave={handleSave}
                    fetchDropdownData={fetchDropdownData}
                    staffList={staffList}
                    customerList={customerList}
                    transporterList={transporterList}
                    hasPermission={hasPermission}
                    isSuperuser={isSuperuser}
                />
            )}
            {isPermissionsModalOpen && (
                <UserPermissionsModal
                    user={permissionsUser}
                    onClose={handleClosePermissionsModal}
                    hasPermission={hasPermission}
                />
            )}
            {isSecurityModalOpen && (
                <div className="modal-overlay">
                    <div className="dialog-container" style={{ width: '900px', maxWidth: '95vw' }}>
                        <div className="dialog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>Security Settings</h3>
                            <button className="icon-btn" onClick={closeSecurityModal} title="Close">
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <div className="dialog-body" style={{ padding: '20px' }}>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                                    Office IP Address <span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    value={securitySettings.company_ip_address}
                                    onChange={handleIPChange}
                                    placeholder="192.168.1.1"
                                    required
                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ marginBottom: '10px' }}>Users Without Outside Access</h4>
                                    <div style={{ border: '1px solid #ddd', borderRadius: '4px', height: '300px', overflowY: 'auto', padding: '10px' }}>
                                        {securityUsers.left.map(user => (
                                            <div key={user.id} style={{ display: 'flex', alignItems: 'center', padding: '5px', borderBottom: '1px solid #eee' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLeftUsers.includes(user.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedLeftUsers([...selectedLeftUsers, user.id]);
                                                        } else {
                                                            setSelectedLeftUsers(selectedLeftUsers.filter(id => id !== user.id));
                                                        }
                                                    }}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                <span>{user.username} ({user.name})</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '100px' }}>
                                    <button
                                        onClick={moveUsersRight}
                                        disabled={selectedLeftUsers.length === 0}
                                        style={{ padding: '8px 12px', cursor: selectedLeftUsers.length === 0 ? 'not-allowed' : 'pointer', opacity: selectedLeftUsers.length === 0 ? 0.5 : 1 }}
                                    >
                                        <i className="fa fa-arrow-right" />
                                    </button>
                                    <button
                                        onClick={moveUsersLeft}
                                        disabled={selectedRightUsers.length === 0}
                                        style={{ padding: '8px 12px', cursor: selectedRightUsers.length === 0 ? 'not-allowed' : 'pointer', opacity: selectedRightUsers.length === 0 ? 0.5 : 1 }}
                                    >
                                        <i className="fa fa-arrow-left" />
                                    </button>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ marginBottom: '10px' }}>Users With Outside Access</h4>
                                    <div style={{ border: '1px solid #ddd', borderRadius: '4px', height: '300px', overflowY: 'auto', padding: '10px' }}>
                                        {securityUsers.right.map(user => (
                                            <div key={user.id} style={{ display: 'flex', alignItems: 'center', padding: '5px', borderBottom: '1px solid #eee' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRightUsers.includes(user.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedRightUsers([...selectedRightUsers, user.id]);
                                                        } else {
                                                            setSelectedRightUsers(selectedRightUsers.filter(id => id !== user.id));
                                                        }
                                                    }}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                <span>{user.username} ({user.name})</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="dialog-footer">
                            <button type="button" className="dialog-btn dialog-btn-secondary" onClick={closeSecurityModal} disabled={savingSecurity}>
                                Cancel
                            </button>
                            <button type="button" className="dialog-btn dialog-btn-primary" onClick={saveSecuritySettings} disabled={savingSecurity}>
                                {savingSecurity ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const UserModal = ({ user, activeTab, onClose, onSave, fetchDropdownData, staffList, customerList, transporterList, hasPermission, isSuperuser }) => {
    const [formData, setFormData] = useState({
        username: '', name: '', email: '', is_active: true, allow_outside_access: false,
        profile: {
            user_type: activeTab,
            linked_staff: null,
            linked_customer: null,
            linked_transporter: null,
            validity_start_date: '',
            validity_end_date: ''
        }
    });
    const [validityDuration, setValidityDuration] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [departmentId, setDepartmentId] = useState('');

    // Fetch departments list for the STAFF department dropdown
    useEffect(() => {
        if (activeTab !== 'STAFF') return;
        (async () => {
            try {
                const res = await fetch(`${API_URL}departments/`, { headers: getAuthHeaders() });
                if (res.ok) setDepartments(await res.json());
            } catch {}
        })();
    }, [activeTab]);

    const VALIDITY_OPTIONS = [
        { id: 'ONE_WEEK', text: 'One Week' },
        { id: 'TWO_WEEKS', text: 'Two Weeks' },
        { id: 'ONE_MONTH', text: 'One Month' },
        { id: 'THREE_MONTHS', text: 'Three Months' },
        { id: 'SIX_MONTHS', text: 'Six Months' },
        { id: 'ONE_YEAR', text: 'One Year' },
        { id: 'PERMANENT', text: 'Permanent' }
    ];

    const isEditing = !!user;
    const isCustomerTab = activeTab === 'CUSTOMER';
    const isTransporterTab = activeTab === 'TRANSPORTER';
    const canSave = isEditing
        ? (isCustomerTab
            ? (hasPermission('CUSTOMER PORTAL', 'can_edit') || hasPermission('USER MANAGEMENT', 'can_edit'))
            : isTransporterTab
                ? (hasPermission('TRANSPORTER USER MANAGEMENT', 'can_edit') || hasPermission('USER MANAGEMENT', 'can_edit'))
                : hasPermission('USER MANAGEMENT', 'can_edit'))
        : (isCustomerTab
            ? (hasPermission('CUSTOMER PORTAL', 'can_create') || hasPermission('USER MANAGEMENT', 'can_create'))
            : isTransporterTab
                ? (hasPermission('TRANSPORTER USER MANAGEMENT', 'can_create') || hasPermission('USER MANAGEMENT', 'can_create'))
                : hasPermission('USER MANAGEMENT', 'can_create'));

    const inferValidityDuration = (startDate, endDate) => {
        if (!endDate) return 'PERMANENT';
        if (!startDate) return '';
        const diffDays = Math.round((new Date(endDate) - new Date(startDate)) / 86400000);
        if (diffDays === 7) return 'ONE_WEEK';
        if (diffDays === 14) return 'TWO_WEEKS';
        if (diffDays >= 28 && diffDays <= 31) return 'ONE_MONTH';
        if (diffDays >= 89 && diffDays <= 92) return 'THREE_MONTHS';
        if (diffDays >= 179 && diffDays <= 184) return 'SIX_MONTHS';
        if (diffDays >= 364 && diffDays <= 366) return 'ONE_YEAR';
        return '';
    };

    // Pre-fill department from linked staff whenever user or staffList changes
    useEffect(() => {
        if (!user || activeTab !== 'STAFF') return;
        const linkedStaffId = user.profile?.linked_staff;
        if (!linkedStaffId) return;
        const staff = staffList.find(s => s.id === linkedStaffId);
        if (staff) setDepartmentId(staff.department ?? '');
    }, [user, staffList, activeTab]);

    useEffect(() => {
        const today = new Date().toISOString().split('T')[0];
        if (user) {
            const startDate = user.profile?.validity_start_date || today;
            const endDate = user.profile?.validity_end_date || '';
            setFormData({
                username: user.username,
                name: user.first_name || '',
                email: user.email,
                is_active: user.is_active,
                allow_outside_access: (activeTab === 'CUSTOMER' || activeTab === 'TRANSPORTER') ? true : (user.allow_outside_access || false),
                profile: {
                    user_type: user.profile?.user_type || activeTab,
                    linked_staff: user.profile?.linked_staff || null,
                    linked_customer: user.profile?.linked_customer || null,
                    linked_transporter: user.profile?.linked_transporter || null,
                    validity_start_date: startDate,
                    validity_end_date: endDate
                }
            });
            setValidityDuration(inferValidityDuration(startDate, endDate));
        } else {
            // Initialize with active tab type
            setFormData(prev => ({
                ...prev,
                allow_outside_access: (activeTab === 'CUSTOMER' || activeTab === 'TRANSPORTER') ? true : prev.allow_outside_access,
                profile: { 
                    ...prev.profile, 
                    user_type: activeTab,
                    validity_start_date: today
                }
            }));
        }
    }, [user, activeTab]);

    const calculateEndDate = (startDate, duration) => {
        if (!startDate || !duration) return '';
        if (duration === 'PERMANENT') return '';

        const date = new Date(startDate);
        switch (duration) {
            case 'ONE_WEEK': date.setDate(date.getDate() + 7); break;
            case 'TWO_WEEKS': date.setDate(date.getDate() + 14); break;
            case 'ONE_MONTH': date.setMonth(date.getMonth() + 1); break;
            case 'THREE_MONTHS': date.setMonth(date.getMonth() + 3); break;
            case 'SIX_MONTHS': date.setMonth(date.getMonth() + 6); break;
            case 'ONE_YEAR': date.setFullYear(date.getFullYear() + 1); break;
            default: return '';
        }
        return date.toISOString().split('T')[0];
    };

    const handleValidityChange = (duration) => {
        setValidityDuration(duration);
        const endDate = calculateEndDate(formData.profile.validity_start_date, duration);
        setFormData(prev => ({
            ...prev,
            profile: { ...prev.profile, validity_end_date: endDate }
        }));
    };

    const handleStartDateChange = (e) => {
        const newStartDate = e.target.value;
        const endDate = calculateEndDate(newStartDate, validityDuration);
        setFormData(prev => ({
            ...prev,
            profile: { 
                ...prev.profile, 
                validity_start_date: newStartDate,
                validity_end_date: endDate || prev.profile.validity_end_date // Keep existing if calc returns empty (e.g. no duration)
            }
        }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name.startsWith('profile.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                profile: { ...prev.profile, [field]: value }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSave) {
            toast.error("You do not have permission to perform this action.");
            return;
        }
        setIsSaving(true);
        const url = user ? `${API_URL}users/${user.id}/` : `${API_URL}users/`;
        const method = user ? 'PUT' : 'POST';

        // Prepare data: Convert empty strings to null for date fields
        const payload = { ...formData };
        if (payload.profile) {
            payload.profile = { ...payload.profile }; // Shallow copy
            if (!payload.profile.validity_end_date) {
                payload.profile.validity_end_date = null;
            }
            if (!payload.profile.validity_start_date) {
                payload.profile.validity_start_date = null;
            }
        }

        try {
            const res = await fetch(url, {
                method, headers: getAuthHeaders(), body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(JSON.stringify(data));

            // If editing a STAFF user, also update the linked staff's department
            if (isEditing && activeTab === 'STAFF') {
                const staffId = formData.profile.linked_staff;
                if (staffId) {
                    await fetch(`${API_URL}staff/${staffId}/`, {
                        method: 'PATCH',
                        headers: getAuthHeaders(),
                        body: JSON.stringify({ department: departmentId !== '' ? departmentId : null }),
                    });
                    // Refresh staff list so the Department column updates immediately
                    fetchDropdownData('STAFF');
                }
            }

            toast.success(`User ${user ? 'updated' : 'created'} successfully!`);
            onSave(data, data.temp_password);
        } catch (error) {
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };
    
    const getLinkOptions = () => {
        switch (formData.profile.user_type) {
            case 'STAFF': return staffList.map(s => ({ id: s.id, text: formatStaffName(s) }));
            case 'CUSTOMER': return customerList.map(c => ({ id: c.id, text: c.name }));
            case 'TRANSPORTER': return transporterList.map(t => ({ id: t.id, text: t.name }));
            default: return [];
        }
    };
    const getLinkValue = () => {
        switch (formData.profile.user_type) {
            case 'STAFF': return formData.profile.linked_staff;
            case 'CUSTOMER': return formData.profile.linked_customer;
            case 'TRANSPORTER': return formData.profile.linked_transporter;
            default: return null;
        }
    };

    const customerEmailsDisplay = useMemo(() => {
        if (formData.profile.user_type !== 'CUSTOMER' || !formData.profile.linked_customer) return '';
        const id = parseInt(formData.profile.linked_customer, 10);
        const customer = customerList.find(c => c.id === id);
        if (!customer || !customer.email_groups || !customer.email_groups.length) return '';
        const emails = ((customer.email_groups[0].emails) || '')
            .split(',').map(e => e.trim()).filter(Boolean);
        return [...new Set(emails)].join(', ');
    }, [customerList, formData.profile.user_type, formData.profile.linked_customer]);

    // Auto-populate name, username and email when a linked entity is selected
    const handleLinkChange = (value) => {
        const userType = formData.profile.user_type;
        const keyMap = { 'STAFF': 'linked_staff', 'CUSTOMER': 'linked_customer', 'TRANSPORTER': 'linked_transporter' };
        const key = keyMap[userType];

        if (!key) return;

        let selectedName = '';
        let selectedEmail = '';
        let selectedUsername = '';
        const idVal = parseInt(value, 10);

        // Find the selected entity details
        if (userType === 'STAFF') {
            const item = staffList.find(s => s.id === idVal);
            if (item) { selectedName = item.name; selectedEmail = item.email; }
        } else if (userType === 'CUSTOMER') {
            const item = customerList.find(c => c.id === idVal);
            if (item) {
                selectedName = item.name;
                selectedUsername = item.tin || '';
                const firstGroupEmails = (((item.email_groups || [])[0] || {}).emails || '')
                    .split(',').map(e => e.trim()).filter(Boolean);
                selectedEmail = [...new Set(firstGroupEmails)][0] || '';
            }
        } else if (userType === 'TRANSPORTER') {
            const item = transporterList.find(t => t.id === idVal);
            if (item) {
                selectedName = item.name;
                selectedUsername = item.tin || '';
                selectedEmail = item.email || '';
            }
        }

        setFormData(prev => ({
            ...prev,
            username: (!isEditing && (userType === 'CUSTOMER' || userType === 'TRANSPORTER') ? (selectedUsername || prev.username) : prev.username),
            name: selectedName || prev.name,
            email: selectedEmail || prev.email,
            profile: {
                ...prev.profile,
                [key]: value,
                linked_staff: key === 'linked_staff' ? value : null,
                linked_customer: key === 'linked_customer' ? value : null,
                linked_transporter: key === 'linked_transporter' ? value : null,
            }
        }));
    };

    const isLinkedUser = formData.profile.user_type !== 'INDEPENDENT';

    return (
        <div className="modal-overlay">
            <div className="dialog-container" style={{ maxWidth: '850px', width: '90%' }}>
                <div className="dialog-header">
                    <h3>{user ? 'Edit User' : `Add New ${activeTab.toLowerCase().replace(/^\w/, c => c.toUpperCase())} User`}</h3>
                    <button onClick={onClose} className="icon-btn" style={{ color: 'white' }} title="Close">&times;</button>
                </div>
                <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
                    <div className="dialog-body">
                        
                        <div className="form-section">
                            <h4>User Details</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                                {isLinkedUser ? (
                                    <>
                                        <div className="form-group">
                                            <label>Link to {formData.profile.user_type} <span className="required"></span></label>
                                            <Select2Dropdown 
                                                options={getLinkOptions()} 
                                                value={getLinkValue()} 
                                                onChange={handleLinkChange} 
                                                onDropdownOpen={() => fetchDropdownData(formData.profile.user_type)} 
                                                displayKey="text" 
                                                valueKey="id" 
                                                required 
                                                isDisabled={!canSave} 
                                                placeholder={`Select ${formData.profile.user_type.toLowerCase()}...`}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Username <span className="required"></span></label>
                                            <input 
                                                type="text" 
                                                name="username" 
                                                value={formData.username} 
                                                onChange={handleChange} 
                                                required 
                                                readOnly={!isEditing && (formData.profile.user_type === 'CUSTOMER' || formData.profile.user_type === 'TRANSPORTER')}
                                                disabled={(!isEditing && (formData.profile.user_type === 'CUSTOMER' || formData.profile.user_type === 'TRANSPORTER')) || (!!user && !isSuperuser)} 
                                                style={{ textTransform: 'none' }}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Email Address</label>
                                            {formData.profile.user_type === 'CUSTOMER' ? (
                                                <input
                                                    type="text"
                                                    name="customer_emails_display"
                                                    value={customerEmailsDisplay}
                                                    readOnly
                                                    disabled
                                                    style={{ backgroundColor: '#f0f0f0' }}
                                                />
                                            ) : (
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    readOnly
                                                    disabled={formData.profile.user_type === 'CUSTOMER' || formData.profile.user_type === 'TRANSPORTER'}
                                                    style={{ backgroundColor: '#f0f0f0' }}
                                                />
                                            )}
                                        </div>
                                        {isEditing && activeTab === 'STAFF' && (
                                            <div className="form-group">
                                                <label>Department</label>
                                                <Select2Dropdown
                                                    options={[{ id: '', text: '— None —' }, ...departments.map(d => ({ id: d.id, text: d.name }))]}
                                                    value={departmentId}
                                                    onChange={setDepartmentId}
                                                    displayKey="text"
                                                    valueKey="id"
                                                    placeholder="Select Department…"
                                                    disabled={!canSave}
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div className="form-group">
                                            <label>Full Name <span className="required"></span></label>
                                            <input type="text" name="name" value={formData.name} onChange={handleChange} required readOnly={!canSave} />
                                        </div>
                                        <div className="form-group">
                                            <label>Username <span className="required"></span></label>
                                            <input 
                                                type="text" 
                                                name="username" 
                                                value={formData.username} 
                                                onChange={handleChange} 
                                                required 
                                                disabled={!!user && !isSuperuser} 
                                                style={{ textTransform: 'none' }}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Email Address <span className="required"></span></label>
                                            <input type="email" name="email" value={formData.email} onChange={handleChange} required readOnly={!canSave} />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        <hr style={{ margin: '20px 0', border: '0', borderTop: '1px solid #eee' }} />

                        <div className="form-section">
                            <h4>Account Validity</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                                <div className="form-group">
                                    <label>Start Date <span className="required"></span></label>
                                    <input 
                                        type="date" 
                                        value={formData.profile.validity_start_date} 
                                        onChange={handleStartDateChange} 
                                        disabled={!canSave}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Validity Duration</label>
                                    <Select2Dropdown
                                        options={VALIDITY_OPTIONS}
                                        value={validityDuration}
                                        onChange={handleValidityChange}
                                        placeholder="Select Duration (Optional)"
                                        isDisabled={!canSave}
                                        valueKey="id"
                                        displayKey="text"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>End Date {validityDuration !== 'PERMANENT' && <span className="required"></span>}</label>
                                    <input 
                                        type="date" 
                                        name="profile.validity_end_date"
                                        value={formData.profile.validity_end_date} 
                                        onChange={handleChange} 
                                        required={validityDuration !== 'PERMANENT'}
                                        disabled={!canSave}
                                    />
                                </div>
                            </div>
                        </div>

                        <hr style={{ margin: '20px 0', border: '0', borderTop: '1px solid #eee' }} />

                        <div className="form-row">
                             <div className="form-group">
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} style={{ marginRight: '8px' }} disabled={!canSave} />
                                    User is Active
                                </label>
                            </div>
                             <div className="form-group">
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input type="checkbox" name="allow_outside_access" checked={formData.allow_outside_access} onChange={handleChange} style={{ marginRight: '8px' }} disabled={!canSave || formData.profile.user_type === 'CUSTOMER' || formData.profile.user_type === 'TRANSPORTER'} />
                                    Access From Outside Office
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="dialog-footer">
                        <button type="button" className="dialog-btn dialog-btn-secondary" onClick={onClose} disabled={isSaving}>Cancel</button>
                        {canSave && (
                            <button type="submit" className="dialog-btn dialog-btn-primary" disabled={isSaving}>
                                {isSaving ? 'Saving...' : (user ? 'Update User' : 'Create User')}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

const UserPermissionsModal = ({ user, onClose, hasPermission }) => {
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [hasExistingUserPerms, setHasExistingUserPerms] = useState(false);

    const isCustomer = user?.profile?.user_type === 'CUSTOMER';
    const isTransporter = user?.profile?.user_type === 'TRANSPORTER';
    const canEdit = hasPermission('USER MANAGEMENT', 'can_edit') || (isTransporter && hasPermission('TRANSPORTER USER MANAGEMENT', 'can_edit'));
    const MODULES = isCustomer ? CLIENT_MENU_MODULES : (isTransporter ? TRANSPORTER_MENU_MODULES : APP_MODULES_HIERARCHICAL);

    const toggleRow = (moduleKey) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(moduleKey)) newExpandedRows.delete(moduleKey);
        else newExpandedRows.add(moduleKey);
        setExpandedRows(newExpandedRows);
    };

    const toggleAllRows = () => {
        const parentModules = MODULES.filter(m => m.level === 0).map(m => m.module);
        if (expandedRows.size === parentModules.length && parentModules.length > 0) {
            setExpandedRows(new Set());
        } else {
            setExpandedRows(new Set(parentModules));
        }
    };

    useEffect(() => {
        if (!user) return;
        const fetchPerms = async () => {
            setLoading(true);
            try {
                let userData = [];
                if (isCustomer) {
                    const res = await fetch(`/api/admin/customer-permissions/?user=${user.id}`, { headers: getAuthHeaders() });
                    userData = await res.json();
                    setHasExistingUserPerms(userData.some(p => p.user === user.id));
                } else if (isTransporter) {
                    const res = await fetch(`/api/admin/transporter-permissions/?user=${user.id}`, { headers: getAuthHeaders() });
                    userData = await res.json();
                    setHasExistingUserPerms(userData.some(p => p.user === user.id));
                } else {
                    const res = await fetch(`/api/admin/user-permissions/?user=${user.id}`, { headers: getAuthHeaders() });
                    userData = await res.json();
                    setHasExistingUserPerms(userData.length > 0);
                }

                let dataToUse = userData;
                if (!isCustomer && !isTransporter && userData.length === 0 && user.profile?.user_type === 'STAFF' && user.profile?.linked_staff) {
                    const staffRes = await fetch(`/api/admin/staff/${user.profile.linked_staff}/`, { headers: getAuthHeaders() });
                    const staffData = await staffRes.json();
                    if (staffData.department) {
                        const deptRes = await fetch(`/api/admin/department-permissions/?department=${staffData.department}`, { headers: getAuthHeaders() });
                        dataToUse = await deptRes.json();
                    }
                }

                const permsMap = MODULES.reduce((acc, moduleInfo) => {
                    const moduleKey = moduleInfo.module;
                    const existing = dataToUse.find(p => p.module === moduleKey);
                    acc[moduleKey] = existing ? { ...existing, user: user.id } : { module: moduleKey, can_create: false, can_view: false, can_edit: false, can_delete: false, user: user.id };
                    return acc;
                }, {});
                setPermissions(permsMap);
            } catch (err) {
                toast.error("Failed to fetch permissions");
            } finally {
                setLoading(false);
            }
        };
        fetchPerms();
    }, [user, isCustomer, MODULES]);

    const handlePermissionChange = (moduleKey, permission, value) => {
        const newPermissions = { ...permissions };
        newPermissions[moduleKey] = { ...newPermissions[moduleKey], [permission]: value };

        const moduleIndex = MODULES.findIndex(m => m.module === moduleKey);
        if (moduleIndex !== -1) {
            if (MODULES[moduleIndex].level === 0) {
                for (let i = moduleIndex + 1; i < MODULES.length; i++) {
                    const childModule = MODULES[i];
                    if (childModule.level > 0) {
                        newPermissions[childModule.module] = { ...newPermissions[childModule.module], [permission]: value };
                    } else break;
                }
            } else {
                let parentIndex = -1;
                for (let i = moduleIndex - 1; i >= 0; i--) {
                    if (MODULES[i].level === 0) {
                        parentIndex = i;
                        break;
                    }
                }
                if (parentIndex !== -1) {
                    const parentKey = MODULES[parentIndex].module;
                    if (value === true) {
                        newPermissions[parentKey] = { ...newPermissions[parentKey], [permission]: true };
                    } else {
                        let anyChildTrue = false;
                        for (let i = parentIndex + 1; i < MODULES.length; i++) {
                            const childModule = MODULES[i];
                            if (childModule.level === 0) break;
                            if (newPermissions[childModule.module] && newPermissions[childModule.module][permission]) {
                                anyChildTrue = true;
                                break;
                            }
                        }
                        if (!anyChildTrue) {
                            newPermissions[parentKey] = { ...newPermissions[parentKey], [permission]: false };
                        }
                    }
                }
            }
        }
        setPermissions(newPermissions);
    };

    const handleSave = async () => {
        if (!canEdit) {
            toast.error("Permission denied.");
            return;
        }
        setSaving(true);
        const url = isCustomer
            ? '/api/admin/customer-permissions/bulk-update/'
            : isTransporter
                ? '/api/admin/transporter-permissions/bulk-update/'
                : '/api/admin/user-permissions/bulk-update/';
        const body = isCustomer ? {
            customer: user.profile?.linked_customer,
            user: user.id,
            permissions: Object.values(permissions).map(p => ({
                module: p.module,
                can_create: p.can_create || false,
                can_view: p.can_view || false,
                can_edit: p.can_edit || false,
                can_delete: p.can_delete || false,
            })),
        } : isTransporter ? {
            transporter: user.profile?.linked_transporter,
            user: user.id,
            permissions: Object.values(permissions).map(p => ({
                module: p.module,
                can_create: p.can_create || false,
                can_view: p.can_view || false,
                can_edit: p.can_edit || false,
                can_delete: p.can_delete || false,
            })),
        } : Object.values(permissions).map(p => ({ ...p, user: user.id }));
        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(body)
            });
            if (res.ok) {
                toast.success(`${isCustomer ? 'Customer' : isTransporter ? 'Transporter' : 'User'} Permissions saved successfully!`);
                onClose();
            } else {
                toast.error('Failed to save permissions');
            }
        } catch (error) {
            toast.error('An error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!canEdit) {
            toast.error("Permission denied.");
            return;
        }
        const confirmMsg = isCustomer
            ? "Are you sure you want to remove custom permissions for this customer user and default to Customer Default Permissions?"
            : "Are you sure you want to remove custom User Permissions and default to Department Permissions?";
        if (!window.confirm(confirmMsg)) {
            return;
        }
        setSaving(true);
        try {
            const url = isCustomer
                ? `/api/admin/customer-permissions/reset/?user=${user.id}`
                : isTransporter
                    ? `/api/admin/transporter-permissions/reset/?user=${user.id}`
                    : `/api/admin/user-permissions/reset/?user=${user.id}`;
            const res = await fetch(url, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            if (res.ok) {
                toast.success(isCustomer
                    ? 'User-specific customer permissions removed. Defaulting to Customer Default Permissions.'
                    : isTransporter
                        ? 'User-specific transporter permissions removed. Defaulting to Transporter Default Permissions.'
                        : 'User Permissions removed. Defaulting to Department Permissions.');
                onClose();
            } else {
                toast.error('Failed to reset permissions');
            }
        } catch (error) {
            toast.error('An error occurred while resetting');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="dialog-container" style={{ maxWidth: '900px', width: '90%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div className="dialog-header">
                    <h3>{isCustomer ? 'Customer Permissions' : isTransporter ? 'Transporter Permissions' : 'User Permissions'} - {user?.username}</h3>
                    <button onClick={onClose} className="icon-btn" style={{ color: 'white' }} title="Close">&times;</button>
                </div>
                <div className="dialog-body" style={{ flexGrow: 1, overflowY: 'auto' }}>
                    {loading ? <p>Loading...</p> : (
                        <>
                            {!hasExistingUserPerms && (
                                <div style={{ marginBottom: 15, padding: 10, backgroundColor: '#eef2f5', borderRadius: 4 }}>
                                    <i className="fa fa-info-circle" style={{ color: '#005f9e', marginRight: 8 }} />
                                    {isCustomer
                                        ? 'Showing customer default permissions. Adjust and save to create customer-specific permissions for this user.'
                                        : isTransporter
                                            ? 'Showing transporter default permissions. Adjust and save to create transporter-specific permissions for this user.'
                                            : 'Showing default Department Permissions. Adjust and save to create custom User Permissions.'}
                                </div>
                            )}
                            <div className="table-responsive">
                                <style>{`
                                    .admin-table tbody tr.hover-highlight:hover {
                                        background-color: #f0f7ff !important;
                                    }
                                    .admin-table tbody tr.parent-row:hover {
                                        background-color: #fff176 !important;
                                    }
                                `}</style>
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={toggleAllRows}>
                                                MODULE
                                                <i className={`fa ${expandedRows.size === MODULES.filter(m => m.level === 0).length && MODULES.length > 0 ? 'fa-chevron-down' : 'fa-chevron-right'}`} style={{ marginLeft: '8px', fontSize: '0.8em' }} />
                                            </th>
                                            <th style={{ textAlign: 'center' }}>VIEW</th>
                                            <th style={{ textAlign: 'center' }}>CREATE</th>
                                            <th style={{ textAlign: 'center' }}>EDIT</th>
                                            <th style={{ textAlign: 'center' }}>DELETE</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {MODULES.map((moduleInfo) => {
                                            const { title, module: moduleKey, level } = moduleInfo;
                                            const perms = permissions[moduleKey] || {};
                                            const isParent = level === 0;
                                            const isExpanded = expandedRows.has(moduleKey);

                                            if (level > 0) {
                                                let parentIndex = MODULES.findIndex(m => m.module === moduleKey);
                                                while (parentIndex > 0 && MODULES[parentIndex].level > 0) parentIndex--;
                                                const parentModuleKey = parentIndex >= 0 ? MODULES[parentIndex].module : null;
                                                if (parentModuleKey && !expandedRows.has(parentModuleKey)) return null;
                                            }

                                            const displayTitle = isParent ? title : `- ${title}`;

                                            return (
                                                <tr key={moduleKey} className={`hover-highlight ${isParent ? 'parent-row' : ''}`} style={{ backgroundColor: isParent ? '#fff9c4' : undefined }}>
                                                    <td style={{
                                                        paddingLeft: `${level * 20 + 10}px`,
                                                        fontWeight: isParent ? 'bold' : 'normal',
                                                        cursor: isParent ? 'pointer' : 'default',
                                                        userSelect: 'none'
                                                    }} onClick={() => isParent && toggleRow(moduleKey)}>
                                                        {isParent && (
                                                            <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} style={{ marginRight: '8px', fontSize: '0.8em' }} />
                                                        )}
                                                        {displayTitle}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input type="checkbox" checked={perms.can_view || false} onChange={e => handlePermissionChange(moduleKey, 'can_view', e.target.checked)} disabled={!canEdit} />
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input type="checkbox" checked={perms.can_create || false} onChange={e => handlePermissionChange(moduleKey, 'can_create', e.target.checked)} disabled={!canEdit} />
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input type="checkbox" checked={perms.can_edit || false} onChange={e => handlePermissionChange(moduleKey, 'can_edit', e.target.checked)} disabled={!canEdit} />
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input type="checkbox" checked={perms.can_delete || false} onChange={e => handlePermissionChange(moduleKey, 'can_delete', e.target.checked)} disabled={!canEdit} />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
                <div className="dialog-footer" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <div>
                        {hasExistingUserPerms && (
                            <button type="button" className="dialog-btn dialog-btn-secondary" style={{ backgroundColor: '#dc3545', color: 'white', borderColor: '#dc3545' }} onClick={handleReset} disabled={saving || loading || !canEdit}>
                                {isCustomer ? 'Reset to Customer Defaults' : isTransporter ? 'Reset to Transporter Defaults' : 'Reset to Department Permissions'}
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button type="button" className="dialog-btn dialog-btn-secondary" onClick={onClose} disabled={saving || loading}>Cancel</button>
                        <button type="button" className="dialog-btn dialog-btn-primary" onClick={handleSave} disabled={saving || loading || !canEdit}>
                            {saving ? 'Saving...' : 'Save Permissions'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserManagement;






